import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Show } from "@clerk/react";
import { toast } from "sonner";
import { ArrowRight, FileCheck2, Loader2, Scale } from "lucide-react";
import { PROFILES } from "@/lib/legal";
import { errMsg, useApi } from "@/lib/api";

const CreateButton = ({ role, slug }) => {
  const api = useApi();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const cls = "inline-flex items-center gap-2 h-14 px-8 rounded-full bg-[#00f2a1] text-[#05130e] font-semibold hover:bg-[#00d890] hover:shadow-[0_0_24px_rgba(0,242,161,0.45)] transition-[background-color,box-shadow] duration-300 disabled:opacity-60";
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api("post", "/companies", { role });
      navigate(`/mis-empresas/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <>
      <Show when="signed-in">
        <button onClick={create} disabled={busy} className={cls} data-testid={`create-profile-${slug}`}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}Crear perfil <ArrowRight className="w-4 h-4" /></button>
      </Show>
      <Show when="signed-out">
        <button onClick={() => navigate("/sign-up")} className={cls} data-testid={`create-profile-${slug}`}>Crear perfil <ArrowRight className="w-4 h-4" /></button>
      </Show>
    </>
  );
};

export default function RoleProfile() {
  const { slug } = useParams();
  const p = PROFILES[slug];
  if (!p) return <Navigate to="/" replace />;
  return (
    <main key={slug} data-testid={`role-profile-${slug}`}>
      <section className="relative bg-[#0a1f18] overflow-hidden grain">
        <div className="absolute inset-0 grid-mask" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#00f2a1] mb-4">{p.eyebrow}</p>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">{p.title}</h1>
            <p className="mt-3 text-lg text-[#00f2a1]">{p.subtitle}</p>
            <p className="mt-5 text-base md:text-lg text-slate-300 max-w-xl">{p.intro}</p>
            <div className="mt-8"><CreateButton role={p.role} slug={slug} /></div>
          </div>
          <img src={p.image} alt="" className="rounded-3xl h-80 w-full object-cover border border-[#1b3d30]" />
        </div>
      </section>
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 space-y-8" data-testid={`legal-framework-${slug}`}>
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#00b37a]">Marco legal chileno · MMA</p>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#05130e] mt-2">Obligaciones normativas principales</h2>
        </div>
        <div className={`grid md:grid-cols-2 ${p.obligations.length === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-5`}>
          {p.obligations.map(([t, ley, d], i) => (
            <article key={t} className="bg-white border border-slate-200 rounded-2xl p-6 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300" data-testid={`obligation-${slug}-${i}`}>
              <span className="font-mono text-xs text-[#00b37a]">0{i + 1}</span>
              <h3 className="font-semibold text-lg text-[#05130e] mt-3 leading-snug">{t}</h3>
              <p className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wide text-[#007a50] bg-[#00f2a1]/10 px-2.5 py-1 rounded-full mt-3"><Scale className="w-3.5 h-3.5" />{ley}</p>
              <p className="text-sm text-slate-600 mt-3 leading-relaxed">{d}</p>
            </article>
          ))}
        </div>
      </section>
      <section className="max-w-[1400px] mx-auto px-5 lg:px-8 pb-20">
        <div className="bg-[#0a1f18] text-white rounded-3xl p-8 grid lg:grid-cols-2 gap-8" data-testid={`required-docs-${slug}`}>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#00f2a1]">Cómo operar en TerraLoop</p>
            <h2 className="text-2xl font-bold mt-2">Documentos para validar tu perfil</h2>
            <p className="text-slate-300 mt-3 text-sm leading-relaxed">Subes cada documento en PDF; la plataforma lee y guarda todos sus datos y tú confirmas que son correctos. {p.platform}</p>
            <div className="mt-6"><CreateButton role={p.role} slug={`${slug}-bottom`} /></div>
          </div>
          <ul className="space-y-3">
            {p.docs.map((d) => <li key={d} className="flex gap-3 bg-white/5 border border-white/10 rounded-2xl p-4 text-sm"><FileCheck2 className="w-5 h-5 shrink-0 text-[#00f2a1]" />{d}</li>)}
          </ul>
        </div>
      </section>
    </main>
  );
}
